import { useRef, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, User, Phone, Car, Loader2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { QuickCaptureVoiceButton } from '@/components/QuickCaptureVoiceButton';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerTrigger,
  DrawerFooter,
} from '@/components/ui/drawer';
import { useAuth } from '@/contexts/AuthContext';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/telemetry';
import { NEXT_STEP_LABELS } from '@/types';
import type { NextStep } from '@/types';

interface Props {
  trigger?: ReactNode;
}

const QUICK_NEXT_STEPS: NextStep[] = ['follow_up', 'appointment', 'call', 'send_numbers'];

/**
 * Quick Capture — the rep's first-tap on a walk-in.
 * 3 fields max → deal created → asks for next step inline.
 * Replaces sticky notes and license copies as the working entry point.
 */
export function QuickCaptureSheet({ trigger }: Props) {
  const navigate = useNavigate();
  const { mode, userId, dealershipId } = useAuth();
  const { refresh } = useDealershipData();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdDealId, setCreatedDealId] = useState<string | null>(null);
  const [savingNext, setSavingNext] = useState<NextStep | null>(null);
  const [scanning, setScanning] = useState(false);
  const [licenseData, setLicenseData] = useState<Record<string, unknown> | null>(null);
  const [licensePath, setLicensePath] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setName(''); setPhone(''); setVehicle(''); setNotes('');
    setCreatedDealId(null); setSavingNext(null);
    setLicenseData(null); setLicensePath(null);
  };

  const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = String(reader.result || '');
      resolve(r.split(',')[1] ?? r);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const handleScan = async (file: File) => {
    if (mode !== 'real') {
      toast.info('License scanning is available in real mode');
      return;
    }
    if (!dealershipId || !userId) {
      toast.error('Sign-in required');
      return;
    }
    setScanning(true);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `${dealershipId}/${userId}/${Date.now()}.${ext}`;
      const upload = await supabase.storage.from('license-scans').upload(path, file, {
        contentType: file.type || 'image/jpeg',
        upsert: false,
      });
      if (upload.error) throw upload.error;
      setLicensePath(path);

      const base64 = await fileToBase64(file);
      const { data, error } = await supabase.functions.invoke('scan-license', {
        body: { image_base64: base64, mime_type: file.type || 'image/jpeg' },
      });
      if (error) throw error;
      const d = (data?.data ?? {}) as Record<string, unknown>;
      setLicenseData(d);
      const fullName = (d.full_name as string) ||
        [d.first_name, d.last_name].filter(Boolean).join(' ');
      if (fullName) setName(fullName);
      toast.success('License scanned', { description: fullName ? `Hi ${fullName}` : 'Review the fields below' });
    } catch (e) {
      toast.error('Scan failed', { description: (e as Error).message });
    } finally {
      setScanning(false);
    }
  };

  const handleCapture = async () => {
    if (!name.trim()) {
      toast.error('Just a name to start');
      return;
    }
    if (mode !== 'real') {
      toast.info('Demo mode — sign in to capture real customers');
      return;
    }
    if (!dealershipId || !userId) {
      toast.error('Sign-in required');
      return;
    }
    setSubmitting(true);
    const payload: Record<string, unknown> = {
      dealership_id: dealershipId,
      rep_id: userId,
      customer_name: name.trim(),
      vehicle: vehicle.trim() || 'TBD',
      phone: phone.trim() || null,
      stage: 'new',
      status: 'active',
      priority: 'warm',
      estimated_value: 0,
      license_image_path: licensePath,
      license_data: licenseData ?? null,
      notes: notes.trim() || null,
    };
    const { data, error } = await supabase
      .from('deals')
      // Cast: new license_* columns added in latest migration; types may lag.
      .insert(payload as never)
      .select('id')
      .maybeSingle();
    setSubmitting(false);
    if (error || !data) {
      toast.error(error?.message ?? 'Could not capture');
      return;
    }
    setCreatedDealId(data.id);
    track('quick_capture', { userId, dealershipId, mode }, { has_phone: !!phone, has_vehicle: !!vehicle });
  };

  const handlePickNext = async (ns: NextStep) => {
    if (!createdDealId) return;
    setSavingNext(ns);
    const { error } = await supabase
      .from('deals')
      .update({ next_step: ns, last_activity_at: new Date().toISOString() })
      .eq('id', createdDealId);
    setSavingNext(null);
    if (error) {
      toast.error('Saved customer, but next step failed', { description: error.message });
    } else {
      toast.success('Captured. Move to the next deal.');
      track('next_step_set', { userId, dealershipId, mode }, { next_step: ns, source: 'quick_capture' });
    }
    refresh();
    const dealId = createdDealId;
    setOpen(false);
    reset();
    navigate(`/deals/${dealId}`);
  };

  return (
    <Drawer open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DrawerTrigger asChild>
        {trigger ?? (
          <Button size="lg" className="w-full h-16 text-base gap-2 font-semibold">
            <Zap className="h-5 w-5" /> Quick Capture
          </Button>
        )}
      </DrawerTrigger>
      <DrawerContent className="max-h-[92vh]">
        <div className="mx-auto w-full max-w-md">
          <DrawerHeader>
            <DrawerTitle className="text-xl">
              {createdDealId ? 'What\u2019s next?' : 'Quick Capture'}
            </DrawerTitle>
            <DrawerDescription>
              {createdDealId
                ? 'Pick a next step so this deal stays alive.'
                : 'Just the basics. We\u2019ll create the deal instantly.'}
            </DrawerDescription>
          </DrawerHeader>

          {!createdDealId ? (
            <div className="px-4 space-y-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleScan(f);
                  e.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full h-14 gap-2"
                disabled={scanning}
                onClick={() => fileRef.current?.click()}
              >
                {scanning ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
                {scanning ? 'Reading license\u2026' : 'Scan driver\u2019s license'}
              </Button>
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-wide text-muted-foreground">
                <div className="flex-1 h-px bg-border" />or type it<div className="flex-1 h-px bg-border" />
              </div>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  autoFocus
                  className="h-14 pl-11 text-base"
                  placeholder="Customer name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="tel"
                  inputMode="tel"
                  className="h-14 pl-11 text-base"
                  placeholder="Phone (optional)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="relative">
                <Car className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  className="h-14 pl-11 text-base"
                  placeholder="Vehicle of interest (optional)"
                  value={vehicle}
                  onChange={(e) => setVehicle(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Notes (optional)</span>
                  <QuickCaptureVoiceButton
                    onTranscript={(t) => setNotes((prev) => (prev ? `${prev} ${t}` : t))}
                    className="h-8 px-2 text-xs gap-1"
                  />
                </div>
                <Textarea
                  className="min-h-[72px] text-sm"
                  placeholder="Trade, timeline, hot buttons…"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center pt-1">
                Don\u2019t remember anything. Just capture them and move on.
              </p>
            </div>
          ) : (
            <div className="px-4 grid grid-cols-2 gap-3">
              {QUICK_NEXT_STEPS.map((ns) => (
                <Button
                  key={ns}
                  size="lg"
                  variant="outline"
                  className="h-16 text-base"
                  disabled={!!savingNext}
                  onClick={() => handlePickNext(ns)}
                >
                  {savingNext === ns ? <Loader2 className="h-5 w-5 animate-spin" /> : NEXT_STEP_LABELS[ns]}
                </Button>
              ))}
            </div>
          )}

          <DrawerFooter>
            {!createdDealId ? (
              <Button
                size="lg"
                className="h-14 text-base gap-2 font-semibold"
                onClick={handleCapture}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Zap className="h-5 w-5" />}
                {submitting ? 'Capturing\u2026' : 'Capture & continue'}
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => { setOpen(false); reset(); navigate(`/deals/${createdDealId}`); }}>
                Skip for now
              </Button>
            )}
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}